import type { DragEvent, ChangeEvent } from "react";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogProps } from "@mui/material";
import type { AudioTranscriptionInfo, TranscriptionModeType } from "./types";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { useAudioTranscription } from "./hook/use-audioTranscription";
import {
  selectAudioFile,
  selectAudioTranscriptionInfo,
  selectIsFileTranscription,
  selectIsFileUpload,
  selectTranscriptionMode,
} from "./redux/selectors";
import {
  setAudioFileAction,
  setAudioTranscriptionInfo,
  setIsFileTranscriptionAction,
  setIsFileUploadAction,
  setTranscriptionModeAction,
} from "./redux/slice";

import styles from "./styles.module.css";
import { ModalBasic } from "../../../shared/components/ModalBasic";
import { UploadZone } from "../../../common/AudioRecognize/components/UploadZone";
import { AudioTranscriptionLayout } from "./components/AudioTranscriotionLayout";
import { TModalsIds } from "../../../app/state/modalSlice/types";
import { useModalSelectors } from "../../../app/state/modalSlice/hook/use-modal-selectors";

interface Props extends Omit<DialogProps, "open"> {
  closeModal: (id: TModalsIds) => void;
}

export const AudioTranscription = ({ closeModal, ...rest }: Props) => {
  const { uploadAudioFile, getProcessingResult } = useAudioTranscription();
  const dispatch = useAppDispatch();
  const soundToTextModal = useModalSelectors("sound_to_text_modal");
  const audioTranscriptionInfo = useAppSelector(selectAudioTranscriptionInfo);
  const isFileUpload = useAppSelector(selectIsFileUpload);
  const isFileTranscription = useAppSelector(selectIsFileTranscription);
  const file = useAppSelector(selectAudioFile);
  const status = useAppSelector(selectTranscriptionMode);
  const [controller, setController] = useState<AbortController | null>(null);
  const { t } = useTranslation();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (!e?.target?.files?.[0]) return;
    dispatch(setAudioFileAction(uploadedFile?.name as string));
    uploadAudioFile(uploadedFile as File);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    const uploadedFile = e.dataTransfer.files[0];
    dispatch(setAudioFileAction(uploadedFile.name));
    uploadAudioFile(uploadedFile as File);
  };

  const handleRecognize = () => {
    dispatch(setTranscriptionModeAction("processing" as TranscriptionModeType));
    const newController = new AbortController();
    setController(newController);
    getProcessingResult(newController.signal);
  };

  const handleAbort = () => {
    dispatch(
      setAudioTranscriptionInfo({
        ...(audioTranscriptionInfo as AudioTranscriptionInfo),
        progress: 0,
        status: "upload",
        text: "",
      }),
    );
    dispatch(setIsFileUploadAction(true));
    dispatch(setIsFileTranscriptionAction(false));
    controller?.abort();
    dispatch(setTranscriptionModeAction("upload" as TranscriptionModeType));
  };

  return (
    <ModalBasic
      open={soundToTextModal.isOpen}
      title={t("audio_transcription.title")}
      hasCloseButton
      className={styles.modal}
      hasFooter={true}
      onOk={handleRecognize}
      onCancel={handleAbort}
      onClose={() => closeModal("sound_to_text_modal")}
      closeModal={() => closeModal("sound_to_text_modal")}
      buttonOkText={t("audio_transcription.recognize_button")}
      disabledOkBtn={isFileUpload || isFileTranscription}
      {...rest}
    >
      <div
        className={classNames(styles["modal-content"], {
          [styles["processing"]]: status !== "upload",
        })}
      >
        {status === "upload" && (
          <UploadZone
            uploadZoneStyle={"default"}
            uploadMessage={t("audio_transcription.upload_instructions")}
            dropHandler={handleDrop}
            handleFileUpload={handleFileChange}
          />
        )}
        {status !== "upload" && (
          <AudioTranscriptionLayout
            status={status}
            content={audioTranscriptionInfo?.text as string}
            fileName={file}
            progress={audioTranscriptionInfo?.progress as number}
          />
        )}
      </div>
    </ModalBasic>
  );
};
